import { convertToPixelCrop } from 'react-image-crop';

/**
 * @param {import('react-image-crop').Crop} crop
 * @param {HTMLImageElement} image
 * @returns {{ x1: number, y1: number, x2: number, y2: number } | null}
 */
export default function cropToNormalized(crop, image) {
  if (!crop || !image || !image.naturalWidth || !image.naturalHeight) return null;
  const vw = image.width;
  const vh = image.height;
  if (!vw || !vh) return null;

  const pixel = convertToPixelCrop(crop, vw, vh);
  const x1 = pixel.x / vw;
  const y1 = pixel.y / vh;
  const x2 = (pixel.x + pixel.width) / vw;
  const y2 = (pixel.y + pixel.height) / vh;

  const clamp = (v) => Math.min(1, Math.max(0, v));
  const nx1 = clamp(x1);
  const ny1 = clamp(y1);
  let nx2 = clamp(x2);
  let ny2 = clamp(y2);
  if (nx2 - nx1 < 0.001) {
    nx2 = Math.min(1, nx1 + 0.001);
  }
  if (ny2 - ny1 < 0.001) {
    ny2 = Math.min(1, ny1 + 0.001);
  }
  return {
    x1: nx1, y1: ny1, x2: nx2, y2: ny2,
  };
}

/**
 * True when crop covers (almost) the full frame — omit from API payload.
 * @param {{ x1: number, y1: number, x2: number, y2: number }} norm
 * @param {number} [epsilon]
 * @returns {boolean}
 */
export function isFullImageCrop(norm, epsilon = 0.002) {
  if (!norm) return true;
  return (
    norm.x1 <= epsilon
    && norm.y1 <= epsilon
    && norm.x2 >= 1 - epsilon
    && norm.y2 >= 1 - epsilon
  );
}

/**
 * Crop rectangle size in source image pixels (natural resolution).
 * @param {import('react-image-crop').Crop} crop
 * @param {HTMLImageElement} image
 * @returns {{ width: number, height: number } | null}
 */
export function cropNaturalPixelSize(crop, image) {
  if (!crop || !image?.naturalWidth || !image?.naturalHeight) return null;
  const vw = image.width;
  const vh = image.height;
  if (!vw || !vh) return null;
  const pixel = convertToPixelCrop(crop, vw, vh);
  const sx = image.naturalWidth / vw;
  const sy = image.naturalHeight / vh;
  return {
    width: Math.max(1, Math.round(pixel.width * sx)),
    height: Math.max(1, Math.round(pixel.height * sy)),
  };
}
